import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/admin/client';

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingId(category.id);
      setForm({
        name: category.name,
        slug: category.slug,
        description: category.description || ''
      });
    } else {
      setEditingId(null);
      setForm({ name: '', slug: '', description: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ name: '', slug: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    try {
      setSaving(true);
      if (editingId) {
        await updateCategory(editingId, form);
      } else {
        await createCategory(form);
      }
      closeModal();
      fetchCategories();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;

    try {
      await deleteCategory(id);
      fetchCategories();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <AdminLayout title="Categories">
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">All Categories</h3>
          <button onClick={() => openModal()} className="admin-btn admin-btn-primary">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Category
          </button>
        </div>
        <div className="admin-card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty-icon">🏷️</div>
              <div className="admin-empty-title">No categories yet</div>
              <button onClick={() => openModal()} className="admin-btn admin-btn-primary" style={{ marginTop: '16px' }}>
                Create Category
              </button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Posts</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id}>
                    <td style={{ fontWeight: 500 }}>{cat.name}</td>
                    <td className="admin-text-muted">{cat.slug}</td>
                    <td>
                      <span className="admin-badge admin-badge-info">
                        {cat.totalCount} posts
                      </span>
                    </td>
                    <td>
                      <div className="admin-flex admin-gap-2">
                        <button onClick={() => openModal(cat)} className="admin-btn admin-btn-sm admin-btn-secondary">
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(cat.id, cat.name)}
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          disabled={cat.totalCount > 0}
                          title={cat.totalCount > 0 ? 'Cannot delete category with posts' : 'Delete'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">{editingId ? 'Edit Category' : 'New Category'}</h3>
              <button onClick={closeModal} className="admin-btn admin-btn-icon admin-btn-secondary">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-card-body">
                <div className="admin-form-group">
                  <label className="admin-label">Name *</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Category name"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Slug</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="category-slug (auto-generated if empty)"
                  />
                </div>
                <div className="admin-form-group admin-mb-0">
                  <label className="admin-label">Description</label>
                  <textarea
                    className="admin-textarea"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional description"
                    style={{ minHeight: '80px' }}
                  />
                </div>
              </div>
              <div className="admin-card-body" style={{ borderTop: '1px solid var(--admin-border)', paddingTop: '16px' }}>
                <div className="admin-flex admin-gap-2 admin-justify-between">
                  <button type="button" onClick={closeModal} className="admin-btn admin-btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
